import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  Zap, 
  Target,
  BarChart3,
  PieChart,
  LineChart,
  Download,
  Calendar,
  Filter,
  RefreshCw
} from "lucide-react";

interface KPIMetric {
  id: string;
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  target: string;
  icon: React.ComponentType<any>;
}

interface ExecutiveInsight {
  title: string;
  priority: 'high' | 'medium' | 'low';
  impact: string;
  recommendation: string;
  category: 'revenue' | 'efficiency' | 'risk' | 'growth';
}

export const BusinessIntelligenceDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['revenue', 'efficiency', 'growth']);

  const executiveKPIs: KPIMetric[] = [
    {
      id: 'revenue',
      title: 'Total Revenue Impact',
      value: '$2.4M',
      change: '+18.7%',
      trend: 'up',
      target: '$2.8M',
      icon: DollarSign
    },
    {
      id: 'efficiency',
      title: 'Operational Efficiency',
      value: '94.2%',
      change: '+5.3%',
      trend: 'up',
      target: '96%',
      icon: Zap
    },
    {
      id: 'customers',
      title: 'Enterprise Clients',
      value: '847',
      change: '+12.4%',
      trend: 'up',
      target: '1,000',
      icon: Users
    },
    {
      id: 'growth',
      title: 'Market Growth Rate',
      value: '23.8%',
      change: '-2.1%',
      trend: 'down',
      target: '25%',
      icon: TrendingUp
    }
  ];

  const executiveInsights: ExecutiveInsight[] = [
    {
      title: 'Revenue Acceleration Opportunity',
      priority: 'high',
      impact: '+$340K projected revenue increase',
      recommendation: 'Expand enterprise automation features',
      category: 'revenue'
    },
    {
      title: 'Efficiency Optimization Gap',
      priority: 'medium',
      impact: '15% processing time reduction potential',
      recommendation: 'Implement AI-powered batch processing',
      category: 'efficiency'
    },
    {
      title: 'Customer Expansion Risk',
      priority: 'high',
      impact: '23% of enterprise clients underutilizing features',
      recommendation: 'Launch customer success program',
      category: 'growth'
    },
    {
      title: 'Competitive Positioning',
      priority: 'medium',
      impact: 'Leading in 3 out of 5 key differentiators',
      recommendation: 'Strengthen API capabilities and integrations',
      category: 'risk'
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'revenue': return DollarSign;
      case 'efficiency': return Zap;
      case 'growth': return TrendingUp;
      case 'risk': return Target;
      default: return BarChart3;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Business Intelligence</h1>
          <p className="text-muted-foreground">
            Executive dashboard with AI-powered insights and strategic recommendations
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Executive KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {executiveKPIs.map((kpi) => (
          <Card key={kpi.id} className="hover-lift">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {kpi.title}
              </CardTitle>
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <kpi.icon className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpi.value}</div>
              <div className="flex items-center justify-between mt-2">
                <div className={`text-xs flex items-center ${
                  kpi.trend === 'up' ? 'text-success' : 
                  kpi.trend === 'down' ? 'text-destructive' : 'text-muted-foreground'
                }`}>
                  {kpi.trend === 'up' ? (
                    <TrendingUp className="h-3 w-3 mr-1" />
                  ) : kpi.trend === 'down' ? (
                    <TrendingDown className="h-3 w-3 mr-1" />
                  ) : null}
                  {kpi.change}
                </div>
                <div className="text-xs text-muted-foreground">
                  Target: {kpi.target}
                </div>
              </div>
              <Progress 
                value={parseFloat(kpi.value.replace(/[^0-9.]/g, '')) / parseFloat(kpi.target.replace(/[^0-9.]/g, '')) * 100} 
                className="h-1 mt-2" 
              />
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="insights" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="insights">Executive Insights</TabsTrigger>
          <TabsTrigger value="performance">Performance Analytics</TabsTrigger>
          <TabsTrigger value="predictions">Predictive Models</TabsTrigger>
          <TabsTrigger value="competitive">Market Intelligence</TabsTrigger>
        </TabsList>

        <TabsContent value="insights" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Strategic Insights</CardTitle>
                <p className="text-sm text-muted-foreground">
                  AI-powered recommendations for executive decision making
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {executiveInsights.map((insight, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">{insight.title}</h4>
                      <Badge variant={getPriorityColor(insight.priority)}>
                        {insight.priority.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-sm text-success font-medium">{insight.impact}</p>
                    <p className="text-sm text-muted-foreground">{insight.recommendation}</p>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const Icon = getCategoryIcon(insight.category);
                        return <Icon className="h-4 w-4 text-primary" />;
                      })()}
                      <span className="text-xs text-muted-foreground capitalize">
                        {insight.category}
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center bg-muted/20 rounded-lg">
                  <div className="text-center">
                    <PieChart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Revenue distribution chart</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center bg-muted/20 rounded-lg">
                  <div className="text-center">
                    <LineChart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Performance trend analysis</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Operational Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center bg-muted/20 rounded-lg">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Operational metrics dashboard</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="predictions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Predictive Analytics</CardTitle>
              <p className="text-sm text-muted-foreground">
                Machine learning models for business forecasting
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-primary">95.2%</div>
                  <div className="text-sm text-muted-foreground">Prediction Accuracy</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-success">$3.2M</div>
                  <div className="text-sm text-muted-foreground">Forecasted Revenue</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-warning">12%</div>
                  <div className="text-sm text-muted-foreground">Growth Rate Projection</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="competitive" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Market Intelligence</CardTitle>
              <p className="text-sm text-muted-foreground">
                Competitive analysis and market positioning insights
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2">Market Position</h4>
                    <div className="text-2xl font-bold text-primary">Top 3</div>
                    <p className="text-sm text-muted-foreground">In enterprise automation</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2">Competitive Advantage</h4>
                    <div className="text-2xl font-bold text-success">87%</div>
                    <p className="text-sm text-muted-foreground">Feature completeness</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};